import { LeasingType } from 'modules/types/LeasingOffer';
import { numberToLocaleString, parseFromCurrencyString } from 'modules/utils';
import { useEffect, useState } from 'react';
import { LeasingCalculatorData } from '../../../common/LeasingCalculatorData';
import {
  calculatedLeasingInstallment,
  getBrutoPrice,
  DEFAULT_PAYMENTS_PER_YEAR,
  PaymentPeriodMap,
} from './helper';
import { LeasingInstallmentState } from './LeasingInstallmentState';

export const useLeasingInstallmentCalculation = (
  defaultValues: LeasingCalculatorData | undefined,
  initialState: LeasingInstallmentState<string>,
  type: LeasingType,
  participationPercentage?: number,
  futureValuePercentage?: number,
) => {
  const [formData, setFormData] =
    useState<LeasingInstallmentState<string>>(initialState);

  const paymentsPerYear =
    PaymentPeriodMap[defaultValues?.leasingPaymentPeriodType?.name || ''] ||
    DEFAULT_PAYMENTS_PER_YEAR;

  useEffect(() => {
    if (
      defaultValues?.leasingNetObjectPrice &&
      defaultValues?.financingPeriod &&
      (defaultValues?.infoInterestRate ||
        defaultValues?.infoInterestRate === 0) &&
      participationPercentage !== undefined &&
      futureValuePercentage !== undefined
    ) {
      const calculatedFormData: LeasingInstallmentState<number> =
        calculatedLeasingInstallment(
          defaultValues.leasingNetObjectPrice,
          defaultValues.leasingObjectPpmv ?? '0',
          defaultValues.financingPeriod.id,
          +defaultValues.infoInterestRate,
          participationPercentage.toString(),
          type,
          futureValuePercentage,
          paymentsPerYear,
        );

      setFormData((prevState) => ({
        ...prevState,
        leasingInstallment: numberToLocaleString(
          calculatedFormData.leasingInstallment,
        ),
        leasingParticipationAmount: numberToLocaleString(
          calculatedFormData.leasingParticipationAmount,
        ),
        futureValuePercentage:
          calculatedFormData.futureValuePercentage?.toString(),
        futureValue: numberToLocaleString(calculatedFormData.futureValue),
      }));
    }
  }, [
    defaultValues?.leasingNetObjectPrice,
    defaultValues?.leasingObjectPpmv,
    defaultValues?.financingPeriod,
    defaultValues?.infoInterestRate,
    defaultValues?.leasingPaymentPeriodType,
    type,
    participationPercentage,
    futureValuePercentage,
    paymentsPerYear,
  ]);

  const getBase = () => {
    const netPrice = defaultValues?.leasingNetObjectPrice;
    if (!netPrice) return undefined;
    const ppmv = +(defaultValues?.leasingObjectPpmv || 0);

    return type === LeasingType.FINANCIAL
      ? getBrutoPrice(netPrice) + ppmv
      : +netPrice + ppmv;
  };

  const recalculateFutureValuePercentage = (
    futureValue: string,
  ): number | undefined => {
    const base = getBase();
    if (base === undefined) return undefined;

    const fvNum = +parseFromCurrencyString(futureValue);
    const pct = base > 0 ? (fvNum / base) * 100 : 0;

    setFormData((prevState) => ({
      ...prevState,
      futureValuePercentage: pct.toFixed(2).toString(),
    }));

    return +pct.toFixed(2);
  };

  const recalculateParticipationPercentage = (
    participationAmount: string,
  ): number | undefined => {
    const base = getBase();
    if (base === undefined) return undefined;

    const amount = +parseFromCurrencyString(participationAmount);
    const pct = base > 0 ? (amount / base) * 100 : 0;

    setFormData((prevState) => ({
      ...prevState,
      leasingParticipationAmount: numberToLocaleString(amount),
    }));

    return +pct.toFixed(2);
  };

  return {
    formData,
    recalculateFutureValuePercentage,
    recalculateParticipationPercentage,
  };
};
